import {
  Badge,
  Box,
  Flex,
  HStack,
  Heading,
  Icon,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
  Tooltip,
} from '@chakra-ui/react'
import { useMemo } from 'react'
import { LuCheck, LuInfo, LuScale, LuTriangleAlert, LuX } from 'react-icons/lu'
import { orderedBandNames, type BandRulesConfig } from '../utils/bandRules'
import {
  agreementOf,
  kappaLabel,
  positiveNegativeAgreement,
  type Agreement,
  type Proportion,
} from '../utils/agreementStats'

import {
  bandPairsOf,
  comparisonRows,
  verdictPairsOf,
  type ComparisonCell,
  type SideReading,
} from '../utils/retrospectivePairs'

/** Horodatage à la minute : une date seule ne permet pas de vérifier l'ordre
 *  des deux lectures quand elles tombent le même jour. */
const fmtStamp = (iso: string): string =>
  new Date(iso).toLocaleString('fr-FR', {
    day: 'numeric', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit',
  })

/** L'écart entre les deux gels, en clair. Négatif = l'instantané IRIS précède
 *  la saisie de paillasse : l'aveugle n'a pas été respecté, et il faut que ça
 *  se voie plutôt que de se deviner. */
const gapLabel = (lockedAt: string, takenAt: string): string => {
  const min = Math.round((new Date(takenAt).getTime() - new Date(lockedAt).getTime()) / 60000)
  if (min < 0) return `⚠ IRIS figé ${Math.abs(min)} min AVANT la paillasse`
  if (min < 1) return 'dévoilé aussitôt'
  if (min < 60) return `dévoilé ${min} min après`
  if (min < 60 * 48) return `dévoilé ${Math.round(min / 60)} h après`
  return `dévoilé ${Math.round(min / 1440)} j après`
}

interface RetrospectiveComparisonProps {
  immunoblotConfig: BandRulesConfig | null
  /** Ce que le biologiste a vu à la paillasse, figé avant le dévoilement. */
  benchBlots: Record<string, SideReading>
  /** Ce qu'IRIS a conclu, figé au dévoilement. */
  irisBlots: Record<string, SideReading>
  /** Pour nommer les bandelettes autrement que par leur numéro. */
  stripLabels: Record<number, string>
  lockedAt?: string
  /** Date du gel de l'instantané IRIS. Les DEUX horodatages sont affichés :
   *  l'écart entre eux est ce qui permet de tester la confusion par le temps
   *  — une lecture de paillasse figée juste avant le dévoilement n'a pas la
   *  même valeur qu'une lecture figée la veille. */
  takenAt?: string
  /** Ce que le biologiste a dû reprendre à la main sur ce cas : bandes
   *  ajoutées, bandes supprimées, boîtes repositionnées. Ce n'est pas une
   *  mesure d'accord — c'est le travail qu'IRIS n'a pas fait tout seul, et
   *  c'est la métrique qui parle de l'outil plutôt que de la méthode. */
  manualCorrections?: { placed: number; suppressed: number; repositioned: number }
}

// L'extraction des paires vit dans utils/retrospectivePairs, partagée avec le
// registre d'évaluation : la fiche d'un cas et l'agrégat multi-cas ne peuvent
// pas se contredire s'ils comptent avec le même code.
type Cell = ComparisonCell

/**
 * La lecture visuelle face à celle d'IRIS, une fois les deux figées.
 *
 * C'est la raison d'être du mode rétrospectif : produire le chiffre d'accord
 * qui manque au dossier. Deux niveaux, parce qu'ils ne disent pas la même
 * chose — l'accord des CONCLUSIONS est ce qui compte pour le rendu au
 * clinicien, l'accord BANDE À BANDE est ce qui explique pourquoi il manque.
 *
 * Les deux colonnes viennent d'instantanés enregistrés, jamais d'un calcul
 * refait à l'affichage : les gabarits de bandes et les seuils d'IRIS changent,
 * et une concordance qui se recalcule n'est pas une mesure.
 */
export default function RetrospectiveComparison({
  immunoblotConfig,
  benchBlots,
  irisBlots,
  stripLabels,
  lockedAt,
  takenAt,
  manualCorrections,
}: RetrospectiveComparisonProps) {
  const bandNames = useMemo(() => orderedBandNames(immunoblotConfig), [immunoblotConfig])

  const rows = useMemo(
    () => comparisonRows(benchBlots, irisBlots, bandNames).map((r) => ({
      ...r,
      label: stripLabels[r.id] ?? `Blot ${r.id}`,
    })),
    [benchBlots, irisBlots, bandNames, stripLabels],
  )

  const totals = useMemo(() => {
    const missed = rows.reduce((s, r) => s + r.cells.filter((c) => c.state === 'manquee').length, 0)
    const added = rows.reduce((s, r) => s + r.cells.filter((c) => c.state === 'ajoutee').length, 0)

    // Deux accords, deux dénominateurs : les conclusions se comptent par
    // bandelette, les bandes par bandelette ET par bande. Le second est le
    // plus nourri, et c'est souvent le seul qui ait assez d'observations pour
    // qu'un kappa veuille dire quelque chose sur un cas isolé.
    const verdictPairs = verdictPairsOf(rows)
    const bandPairs = bandPairsOf(rows)
    return {
      strips: rows.length,
      verdict: agreementOf(verdictPairs),
      band: agreementOf(bandPairs),
      // Sur les BANDES : « des bandes que vous avez vues, IRIS en retrouve
      // tant » — la question que se pose un biologiste, et celle qui dit si
      // l'outil rate des choses ou en invente.
      pna: positiveNegativeAgreement(bandPairs),
      // Les mêmes deux accords sur les CONCLUSIONS : « des bandelettes que
      // vous avez conclues positives, IRIS en conclut autant ». C'est le
      // chiffre qui parle au clinicien, l'autre explique pourquoi.
      pnaVerdict: positiveNegativeAgreement(verdictPairs),
      missed,
      added,
    }
  }, [rows])

  // Accord positif et négatif BANDE PAR BANDE. Les taux d'ensemble disent
  // qu'il y a un désaccord ; ceux-ci disent LAQUELLE le porte, ce qui est la
  // seule forme actionnable : une P31 systématiquement manquée se corrige,
  // un « 87 % toutes bandes confondues » ne se corrige pas. Même calcul que
  // les taux globaux, restreint aux observations d'une seule bande — d'où
  // des effectifs petits, affichés avec chaque taux.
  /**
   * Stratification par intensité — et son sens exact, qui n'est pas évident.
   *
   * Seul IRIS gradue : le biologiste voit une bande ou ne la voit pas. On ne
   * peut donc PAS stratifier « les bandes vues à la paillasse ». Ce qui se
   * stratifie, c'est ce qu'IRIS a retenu : parmi les bandes qu'il retient,
   * quelle part la paillasse confirme — séparément pour les faibles et les
   * franches. C'est la question qui compte pour corriger la détection, parce
   * qu'elle teste directement l'hypothèse que les bandes ajoutées à tort sont
   * surtout des bandes faibles.
   *
   * Le seuil est la moitié de l'intensité de la même bande sur le témoin.
   * Arbitraire comme tout seuil, mais lisible et vérifiable à l'œil.
   */
  const FAINT_BELOW = 0.5
  const byIntensity = useMemo(() => {
    const buckets = { faint: { kept: 0, confirmed: 0 }, strong: { kept: 0, confirmed: 0 } }
    let graded = 0
    rows.forEach((r) => r.cells.forEach((c) => {
      if (c.state !== 'accord' && c.state !== 'ajoutee') return   // IRIS ne l'a pas retenue
      if (typeof c.irisRatio !== 'number') return                 // cas figé sans intensité
      graded += 1
      const b = c.irisRatio < FAINT_BELOW ? buckets.faint : buckets.strong
      b.kept += 1
      if (c.state === 'accord') b.confirmed += 1
    }))
    return { ...buckets, graded }
  }, [rows])

  const perBand = useMemo(
    () => bandNames.map((band) => {
      const pairs = rows.map((r) => {
        const cell = r.cells.find((c) => c.band === band)
        return [
          cell?.state === 'accord' || cell?.state === 'manquee',
          cell?.state === 'accord' || cell?.state === 'ajoutee',
        ] as [boolean, boolean]
      })
      return { band, ...positiveNegativeAgreement(pairs) }
    }),
    [rows, bandNames],
  )

  if (!rows.length) return null

  const cellIcon = (state: Cell | null) => {
    if (state === 'accord') return { icon: LuCheck, color: 'green.500', label: 'vue des deux côtés' }
    if (state === 'manquee') return { icon: LuX, color: 'red.500', label: 'vue à la paillasse, pas par IRIS' }
    if (state === 'ajoutee') return { icon: LuTriangleAlert, color: 'orange.500', label: 'vue par IRIS, pas à la paillasse' }
    return null
  }

  return (
    <Box
      bg="white"
      borderRadius="2xl"
      border="1px solid"
      borderColor="gray.100"
      boxShadow="0 10px 30px -12px rgba(124, 58, 237, 0.15)"
      p={{ base: 4, md: 6 }}
      mb={6}
    >
      <Flex justify="space-between" align="start" wrap="wrap" gap={3} mb={5}>
        <HStack spacing={3}>
          <Flex w="40px" h="40px" borderRadius="xl" bgGradient="linear(to-br, purple.400, blue.400)" align="center" justify="center">
            <Icon as={LuScale} color="white" boxSize={5} />
          </Flex>
          <Box>
            <Heading size="md">Lecture visuelle contre IRIS</Heading>
            <Text fontSize="sm" color="gray.500">
              {totals.strips} bandelette{totals.strips > 1 ? 's' : ''} comparée{totals.strips > 1 ? 's' : ''}
              {/* Les deux horodatages, à la minute, et l'écart entre eux :
                  c'est lui qui permet de vérifier après coup que la lecture
                  de paillasse a bien précédé le dévoilement, et de combien.
                  Une date seule ne le disait pas. */}
              {lockedAt ? ` · paillasse figée le ${fmtStamp(lockedAt)}` : ''}
              {takenAt ? ` · IRIS figé le ${fmtStamp(takenAt)}` : ''}
              {lockedAt && takenAt ? ` (${gapLabel(lockedAt, takenAt)})` : ''}
              {/* Comment l'aveugle a été obtenu reste ENREGISTRÉ sur le cas
                  (benchReading.blinding) — il faudra séparer les deux
                  populations à l'analyse, et sur une publication l'un des deux
                  ne tient pas. Mais au niveau d'un cas, c'est une mention qui
                  ne change rien à ce que le biologiste fait et qui se lit
                  comme un reproche. Elle a sa place dans la vue d'ensemble et
                  dans l'export, pas ici. */}
            </Text>
          </Box>
        </HStack>
      </Flex>

      {/* La PAIRE d'abord, l'accord global ensuite.
          
          « FDA discourages the stand-alone use of measures of overall
          agreement » : avec dix bandes dont une ou deux positives, l'accord
          global dépasse 90 % même si IRIS rate la moitié des bandes vues.
          C'est l'accord positif qui le dit, et c'est donc lui qu'on montre
          en premier. Chaque taux porte son intervalle et son dénominateur :
          « 100 % » sur quatre bandelettes et « 100 % » sur quatre cents ne
          disent pas la même chose. */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={3}>
        <Rate
          label="Accord positif · bandes"
          p={totals.pna.positive}
          unit="bande vue"
          tone="teal"
          info={"Ce que c'est : parmi les bandes que VOUS avez vues, la part qu'IRIS retrouve aussi.\n\nCalcul : a / (a + c), où a = bandes vues des deux côtés et c = bandes que vous seul avez vues. Le dénominateur ne compte que vos bandes positives.\n\nIntervalle : méthode du score (Wilson) à 95 %, conditionnel à votre lecture.\n\nArithmétiquement c'est une sensibilité — on ne l'appelle pas ainsi, car ce serait supposer que la lecture visuelle ne se trompe jamais."}
        />
        <Rate
          label="Accord négatif · bandes"
          p={totals.pna.negative}
          unit="absence"
          tone="blue"
          info={"Ce que c'est : parmi les bandes que vous n'avez PAS vues, la part qu'IRIS ne voit pas non plus.\n\nCalcul : d / (d + b), où d = bandes absentes des deux côtés et b = bandes qu'IRIS a retenues sans que vous les voyiez.\n\nIntervalle : Wilson à 95 %.\n\nC'est le pendant d'une spécificité, relative à votre lecture et non à une vérité."}
        />
        <Rate
          label="Accord positif · conclusions"
          p={totals.pnaVerdict.positive}
          unit="conclusion positive"
          tone="teal"
          info={"Ce que c'est : parmi les bandelettes que vous avez conclues POSITIVES, la part qu'IRIS conclut positives aussi.\n\nCalcul : a / (a + c) sur les conclusions, une observation par bandelette.\n\nLes deux conclusions sortent des mêmes règles de la notice, appliquées d'un côté à vos bandes, de l'autre à celles d'IRIS : un écart vient toujours des bandes, jamais d'une lecture différente du mode d'emploi."}
        />
        <Rate
          label="Accord négatif · conclusions"
          p={totals.pnaVerdict.negative}
          unit="conclusion négative"
          tone="blue"
          info={"Ce que c'est : parmi les bandelettes que vous avez conclues NÉGATIVES, la part qu'IRIS conclut négatives aussi.\n\nCalcul : d / (d + b) sur les conclusions.\n\nSur une série où presque tout est négatif, ce chiffre est très élevé par construction : c'est l'accord positif qui porte l'information."}
        />
      </SimpleGrid>

      {/* Les quatre effectifs, parce que la FDA demande de rapporter le
          tableau lui-même et pas seulement ce qu'on en tire : deux tableaux
          très différents peuvent donner le même accord global. */}
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mb={3}>
        <TwoByTwo
          title="Tableau 2×2 · bandes"
          a={totals.band}
          info={"Les quatre effectifs dont sortent tous les taux ci-dessus, une ligne par bande et par bandelette.\n\na = vue des deux côtés · b = vue par IRIS seul · c = vue par vous seul · d = absente des deux côtés.\n\nAccord positif = a/(a+c), accord négatif = d/(d+b), accord global = (a+d)/n."}
        />
        <TwoByTwo
          title="Tableau 2×2 · conclusions"
          a={totals.verdict}
          info={"Le même tableau, une ligne par bandelette comparée, sur la conclusion rendue.\n\na = positive des deux côtés · b = positive pour IRIS seul · c = positive pour vous seul · d = négative des deux côtés."}
        />
      </SimpleGrid>

      {/* Stratification par intensité : n'apparaît que sur les cas dont
          l'instantané porte les intensités (depuis novembre 2026). Sur les
          cas antérieurs, elle n'est pas reconstructible — le dire plutôt que
          d'afficher un bloc vide. */}
      {byIntensity.graded > 0 && (
        <Box borderWidth="1px" borderColor="gray.100" borderRadius="xl" p={3} mb={3}>
          <MetricLabel
            label="Bandes retenues par IRIS, confirmées à la paillasse"
            info={"Ce que c'est : parmi les bandes qu'IRIS a retenues, la part que vous avez vue aussi — séparément pour les bandes faibles et les franches.\n\nPourquoi dans ce sens : vous ne graduez pas vos bandes, vous les voyez ou non. Seule l'intensité d'IRIS existe, donc seule sa sélection peut être stratifiée. C'est aussi la question utile : les bandes ajoutées à tort sont-elles surtout des bandes faibles ?\n\nSeuil : une bande est dite faible en dessous de la moitié de l'intensité de la MÊME bande sur le témoin de la plaque. Le rapport au témoin s'affranchit du scan et du lot.\n\nUn écart marqué entre les deux colonnes désigne un réglage de détection, pas un désaccord de lecture."}
          />
          <SimpleGrid columns={2} spacing={3} mt={2}>
            {([
              ['Bandes faibles', byIntensity.faint, 'orange'],
              ['Bandes franches', byIntensity.strong, 'teal'],
            ] as Array<[string, { kept: number; confirmed: number }, string]>).map(([label, b, tone]) => (
              <Box key={label}>
                <Text fontSize="2xs" color="gray.500">{label}</Text>
                {b.kept === 0 ? (
                  <Text fontSize="sm" color="gray.400">aucune</Text>
                ) : (
                  <>
                    <HStack align="baseline" spacing={1.5}>
                      <Text fontSize="xl" fontWeight="700" lineHeight="1.1" color={`${tone}.600`}>
                        {Math.round((b.confirmed / b.kept) * 100)} %
                      </Text>
                      <Text fontSize="2xs" color="gray.500">confirmées</Text>
                    </HStack>
                    <Text fontSize="2xs" color="gray.500">
                      {b.confirmed} sur {b.kept} retenue{b.kept > 1 ? 's' : ''}
                    </Text>
                  </>
                )}
              </Box>
            ))}
          </SimpleGrid>
          <Text fontSize="2xs" color="gray.400" mt={1.5}>
            Seuil : moins de la moitié de l'intensité de la même bande sur le témoin.
            Les bandes qu'IRIS n'a pas retenues n'entrent pas dans ce calcul — il n'y a
            pas d'intensité à leur rapporter.
          </Text>
        </Box>
      )}
      {byIntensity.graded === 0 && rows.length > 0 && (
        <Text fontSize="2xs" color="gray.400" mb={3}>
          Stratification par intensité indisponible sur ce cas : son instantané a été figé
          avant que les intensités n'y soient portées, ou aucun témoin n'était désigné sur la
          plaque. Elle apparaîtra sur les cas figés désormais.
        </Text>
      )}

      {/* Le kappa, parce qu'un accord brut élevé sur une série presque
          entièrement négative ne mesure que la rareté des positifs. */}
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mb={5}>
        <Kappa
          title="Kappa des conclusions"
          a={totals.verdict}
          info={"Ce que c'est : l'accord des conclusions une fois retiré l'accord qui serait survenu par hasard.\n\nCalcul : (Po − Pe) / (1 − Pe). Po = accord observé = (a+d)/n. Pe = accord attendu si chacun répondait au hasard en gardant sa propre fréquence de « oui » : Pe = p₁p₂ + (1−p₁)(1−p₂).\n\nIntervalle : erreur type de Fleiss, ± 1,96 SE.\n\nRepères de Landis et Koch : < 0,40 faible · 0,41–0,60 modéré · 0,61–0,80 substantiel · au-delà presque parfait.\n\nIl dépend de la prévalence : sur une série presque toute négative il chute même quand l'accord est bon. À lire avec la paire d'accords, jamais seul."}
        />
        <Kappa
          title="Kappa bande à bande"
          a={totals.band}
          info={"Le même calcul, (Po − Pe) / (1 − Pe), mais une observation par bande et par bandelette.\n\nIl repose sur beaucoup plus d'observations que celui des conclusions — sans que cela le rende proportionnellement plus précis : les bandes d'une même plaque partagent le scan, le lot et la calibration de la ligne de contrôle, donc elles ne sont pas indépendantes. L'intervalle affiché suppose qu'elles le sont : il est donc plus étroit que la réalité.\n\nSur plusieurs plaques, l'analyse d'ensemble devra rechantillonner par plaque entière."}
        />
      </SimpleGrid>

      {manualCorrections && (
        <Box borderWidth="1px" borderColor="gray.100" borderRadius="xl" p={3} mb={5}>
          <Box mb={1}>
            <MetricLabel
              label="Reprises à la main sur ce cas"
              info="Ce que vous avez dû corriger vous-même : bandes ajoutées, bandes supprimées, boîtes de détection repositionnées. Ce n’est pas une mesure d’accord — c’est le travail qu’IRIS n’a pas fait tout seul, et c’est ce qui dit si l’outil fait gagner du temps."
            />
          </Box>
          <HStack spacing={5} fontSize="sm" color="gray.700" wrap="wrap">
            <Text><b>{manualCorrections.placed}</b> bande{manualCorrections.placed > 1 ? 's' : ''} ajoutée{manualCorrections.placed > 1 ? 's' : ''}</Text>
            <Text><b>{manualCorrections.suppressed}</b> supprimée{manualCorrections.suppressed > 1 ? 's' : ''}</Text>
            <Text><b>{manualCorrections.repositioned}</b> boîte{manualCorrections.repositioned > 1 ? 's' : ''} repositionnée{manualCorrections.repositioned > 1 ? 's' : ''}</Text>
          </HStack>
          <Text fontSize="xs" color="gray.500" mt={1}>
            Ce n’est pas une mesure d’accord : c’est le travail qu’IRIS n’a pas fait tout seul.
          </Text>
        </Box>
      )}

      <Box overflowX="auto">
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>Bandelette</Th>
              {bandNames.map((band) => (
                <Th key={band} textAlign="center">{band}</Th>
              ))}
              <Th>Paillasse</Th>
              <Th>IRIS</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row) => (
              <Tr key={row.id} bg={row.verdictAgrees ? undefined : 'red.50'}>
                <Td fontWeight="600">{row.label}</Td>
                {row.cells.map((cell) => {
                  const look = cellIcon(cell.state)
                  return (
                    <Td key={cell.band} textAlign="center">
                      {look ? (
                        <Tooltip label={look.label} hasArrow>
                          <span><Icon as={look.icon} color={look.color} boxSize={4} /></span>
                        </Tooltip>
                      ) : (
                        <Text color="gray.300">·</Text>
                      )}
                    </Td>
                  )
                })}
                <Td>
                  <Badge colorScheme={row.benchVerdict === 'POSITIF' ? 'green' : 'gray'}>
                    {row.benchVerdict ?? '—'}
                  </Badge>
                </Td>
                <Td>
                  <HStack spacing={1}>
                    <Badge colorScheme={row.irisVerdict === 'POSITIF' ? 'green' : 'gray'}>
                      {row.irisVerdict ?? '—'}
                    </Badge>
                    {!row.verdictAgrees && row.irisVerdict != null && (
                      <Icon as={LuTriangleAlert} color="red.500" boxSize={3.5} />
                    )}
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
          {/* Les deux taux, bande par bande, sous la colonne de chaque bande :
              c'est la lecture qui désigne la bande à corriger. Un tiret
              signale qu'aucune observation ne nourrit le taux pour cette
              bande (jamais vue de ce côté), ce qui n'est pas un zéro. */}
          <Tfoot>
            <Tr>
              <Th fontSize="2xs" textTransform="none" color="teal.700">
                <Tooltip
                  label={"Par bande : parmi les fois où VOUS avez vu cette bande, la part où IRIS l'a vue aussi. Entre parenthèses, le nombre de fois où vous l'avez vue — c'est lui qui dit si le taux veut dire quelque chose."}
                  hasArrow
                >
                  <span>IRIS retrouve</span>
                </Tooltip>
              </Th>
              {perBand.map(({ band, positive }) => (
                <Th key={band} textAlign="center" fontSize="2xs" textTransform="none"
                  color={positive && positive.value < 1 ? 'red.600' : 'gray.600'}>
                  {positive ? `${Math.round(positive.value * 100)} % (${positive.n})` : '—'}
                </Th>
              ))}
              <Th colSpan={2} />
            </Tr>
            <Tr>
              <Th fontSize="2xs" textTransform="none" color="blue.700">
                <Tooltip
                  label={"Par bande : parmi les fois où vous n'avez PAS vu cette bande, la part où IRIS ne l'a pas vue non plus. Un taux bas désigne une bande qu'IRIS ajoute trop souvent."}
                  hasArrow
                >
                  <span>Absences confirmées</span>
                </Tooltip>
              </Th>
              {perBand.map(({ band, negative }) => (
                <Th key={band} textAlign="center" fontSize="2xs" textTransform="none"
                  color={negative && negative.value < 1 ? 'orange.600' : 'gray.600'}>
                  {negative ? `${Math.round(negative.value * 100)} % (${negative.n})` : '—'}
                </Th>
              ))}
              <Th colSpan={2} />
            </Tr>
          </Tfoot>
        </Table>
      </Box>

      <HStack spacing={4} mt={4} fontSize="xs" color="gray.500" wrap="wrap">
        <HStack spacing={1}><Icon as={LuCheck} color="green.500" /><Text>vue des deux côtés</Text></HStack>
        <HStack spacing={1}><Icon as={LuX} color="red.500" /><Text>manquée par IRIS</Text></HStack>
        <HStack spacing={1}><Icon as={LuTriangleAlert} color="orange.500" /><Text>vue par IRIS seulement</Text></HStack>
        <Text>· un point : absente des deux côtés</Text>
      </HStack>
    </Box>
  )
}

/**
 * L'intitulé d'une mesure, avec sa définition sous un « i ».
 *
 * Ces chiffres se lisent mal sans savoir ce qu'ils comptent : un accord à
 * 100 % n'a pas le même sens selon qu'il porte sur deux bandelettes ou sur
 * quatre cents comparaisons de bandes. La définition dit le dénominateur et
 * ce qui est comparé, en français de paillasse plutôt qu'en statistiques.
 */
function MetricLabel({ label, info }: { label: string; info?: string }) {
  return (
    <HStack spacing={1} align="center">
      <Text fontSize="2xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">
        {label}
      </Text>
      {info && (
        <Tooltip
          hasArrow
          placement="top"
          maxW="340px"
          label={info}
          openDelay={150}
          // Les définitions tiennent en plusieurs paragraphes — ce que mesure
          // le chiffre, puis son calcul. Sans cela tout se colle en un bloc.
          whiteSpace="pre-line"
        >
          <Box as="span" display="inline-flex" cursor="help">
            <Icon as={LuInfo} boxSize={3} color="gray.400" />
          </Box>
        </Tooltip>
      )}
    </HStack>
  )
}

/**
 * Un taux tel qu'il doit se lire : avec son intervalle et son dénominateur.
 *
 * La FDA demande les mesures d'accord « and corresponding confidence
 * intervals », et le dénominateur avec — un accord de 100 % sur trois
 * bandes vues n'est pas un résultat, c'est une coïncidence, et seul le
 * « n = 3 » permet de le voir.
 */
function Rate({
  label,
  p,
  unit,
  tone,
  info,
}: { label: string; p: Proportion | null; unit: string; tone: string; info?: string }) {
  // « 2 bande vues » : le pluriel ne portait que sur le dernier mot. En
  // français l'unité est un groupe — « bande vue », « conclusion négative »
  // — dont chaque mot s'accorde.
  const plural = (n: number, words: string) =>
    n > 1 ? words.split(' ').map((w) => `${w}s`).join(' ') : words
  return (
    <Box borderWidth="1px" borderColor="gray.100" borderRadius="xl" p={3}>
      <MetricLabel label={label} info={info} />
      {p === null || p.n === 0 ? (
        <Text fontSize="sm" color="gray.500" mt={1}>
          aucune observation de ce côté
        </Text>
      ) : (
        <>
          <HStack align="baseline" spacing={0.5}>
            <Text fontSize="2xl" fontWeight="700" color={`${tone}.600`} lineHeight="1.1">
              {Math.round(p.value * 100)}
            </Text>
            <Text fontSize="sm" color={`${tone}.500`}>%</Text>
          </HStack>
          <Text fontSize="2xs" color="gray.500" mt={0.5}>
            IC 95 % de {Math.round(p.ci[0] * 100)} à {Math.round(p.ci[1] * 100)} %
          </Text>
          <Text fontSize="2xs" color="gray.400">
            sur {p.n} {plural(p.n, unit)}
          </Text>
        </>
      )}
    </Box>
  )
}

/**
 * Le tableau 2×2, que la FDA demande de rapporter tel quel.
 *
 * Deux tableaux très différents donnent le même accord global : seuls les
 * quatre effectifs permettent de refaire les calculs et de voir de quel
 * côté penchent les désaccords.
 */
function TwoByTwo({ title, a, info }: { title: string; a: Agreement; info?: string }) {
  const cell = (n: number, letter: string, tone?: string) => (
    <Td textAlign="center" py={1.5}>
      <Text fontSize="md" fontWeight="700" color={tone ?? 'gray.800'} lineHeight="1.2">{n}</Text>
      <Text fontSize="2xs" color="gray.400">{letter}</Text>
    </Td>
  )
  return (
    <Box borderWidth="1px" borderColor="gray.100" borderRadius="xl" p={3}>
      <MetricLabel label={title} info={info} />
      <Table size="sm" variant="simple" mt={1}>
        <Thead>
          <Tr>
            <Th px={1} py={1} />
            <Th px={1} py={1} textAlign="center" fontSize="2xs">IRIS +</Th>
            <Th px={1} py={1} textAlign="center" fontSize="2xs">IRIS −</Th>
          </Tr>
        </Thead>
        <Tbody>
          <Tr>
            <Td px={1} py={1.5} fontSize="2xs" color="gray.500">Œil +</Td>
            {cell(a.table.bothYes, 'a')}
            {cell(a.table.onlyA, 'c', 'red.600')}
          </Tr>
          <Tr>
            <Td px={1} py={1.5} fontSize="2xs" color="gray.500">Œil −</Td>
            {cell(a.table.onlyB, 'b', 'orange.600')}
            {cell(a.table.bothNo, 'd')}
          </Tr>
        </Tbody>
      </Table>
      {/* L'accord global vit ICI et nulle part ailleurs : il vaut (a+d)/n,
          donc il se lit sur ce tableau. En tuile séparée il comptait une
          deuxième fois des nombres déjà affichés — et il prenait la place
          d'honneur, que la FDA réserve justement à la paire PPA/NPA. Il
          reste parce qu'il est le seul chiffre comparable aux accords
          publiés sur la lecture automatisée de blots (84,7 à 94,2 %). */}
      <Text fontSize="2xs" color="gray.500" mt={1}>
        accord global{' '}
        <Text as="span" fontWeight="700" color="gray.700">
          {Math.round(a.observed * 100)} %
        </Text>
        {a.observedCi
          ? ` [${Math.round(a.observedCi[0] * 100)} – ${Math.round(a.observedCi[1] * 100)}]`
          : ''}
        {' · '}n = {a.n}
      </Text>
    </Box>
  )
}

/**
 * Un kappa tel qu'il doit se lire : jamais seul. Son dénominateur, son
 * intervalle, et le mot qui le qualifie — ou, à défaut, la raison pour
 * laquelle il n'existe pas, qui vaut mieux qu'une case vide.
 */
function Kappa({ title, a, info }: { title: string; a: Agreement; info?: string }) {
  return (
    <Box borderWidth="1px" borderColor="gray.100" borderRadius="xl" p={3}>
      <MetricLabel label={title} info={info} />
      {a.kappa === null ? (
        <Text fontSize="sm" color="gray.500" mt={1}>
          non calculable — {a.undefinedReason}
        </Text>
      ) : (
        <>
          <HStack align="baseline" spacing={2}>
            <Text fontSize="2xl" fontWeight="700" lineHeight="1.1">
              {a.kappa.toFixed(2)}
            </Text>
            <Badge colorScheme={a.kappa >= 0.61 ? 'green' : a.kappa >= 0.41 ? 'orange' : 'red'}>
              {kappaLabel(a.kappa)}
            </Badge>
          </HStack>
          <Text fontSize="2xs" color="gray.500" mt={0.5}>
            {a.ci
              ? `IC 95 % de ${a.ci[0].toFixed(2)} à ${a.ci[1].toFixed(2)} · n = ${a.n}`
              : a.ciNote}
          </Text>
          {/* La prévalence des positifs de chaque côté, sans laquelle le
              kappa ne s'interprète pas : le même 0,45 ne veut pas dire la
              même chose sur une série à 50 % de positifs et sur une série à
              5 %. Les deux marges sortent du tableau 2×2 déjà calculé. */}
          <Text fontSize="2xs" color="gray.500">
            Positifs : paillasse {pctOf(a.table.bothYes + a.table.onlyA, a.n)} · IRIS{' '}
            {pctOf(a.table.bothYes + a.table.onlyB, a.n)}
          </Text>
        </>
      )}
    </Box>
  )
}

/** Part en pourcentage entier, ou un tiret quand il n'y a rien à diviser. */
const pctOf = (k: number, n: number): string => (n > 0 ? `${Math.round((k / n) * 100)} %` : '—')
